import type { AutomationPriority, AutomationTask } from '../types'

/**
 * JobQueue — priority queue with cancel / reschedule / retries metadata.
 */
export class JobQueue {
  private readonly tasks: AutomationTask[] = []

  enqueue(
    input: Omit<
      AutomationTask,
      'id' | 'status' | 'createdAt' | 'startedAt' | 'finishedAt' | 'duration' | 'error' | 'attempts'
    > & { id?: string },
  ): AutomationTask {
    const task: AutomationTask = {
      id: input.id ?? crypto.randomUUID(),
      supplier: input.supplier,
      operation: input.operation,
      priority: input.priority,
      payload: input.payload,
      status: 'QUEUED',
      createdAt: new Date().toISOString(),
      startedAt: null,
      finishedAt: null,
      duration: null,
      error: null,
      attempts: 0,
      maxAttempts: input.maxAttempts,
    }
    this.tasks.push(task)
    this.sort()
    return task
  }

  dequeue(): AutomationTask | null {
    const index = this.tasks.findIndex((t) => t.status === 'QUEUED' || t.status === 'RETRYING')
    if (index < 0) return null
    const [task] = this.tasks.splice(index, 1)
    return task ?? null
  }

  peek(): AutomationTask[] {
    return this.tasks
      .filter((t) => t.status === 'QUEUED' || t.status === 'RETRYING')
      .sort((a, b) => b.priority - a.priority || a.createdAt.localeCompare(b.createdAt))
  }

  cancel(taskId: string): AutomationTask | null {
    const task = this.tasks.find((t) => t.id === taskId)
    if (!task) return null
    if (task.status !== 'QUEUED' && task.status !== 'RETRYING') return null
    task.status = 'CANCELLED'
    task.finishedAt = new Date().toISOString()
    return task
  }

  reschedule(taskId: string, priority?: AutomationPriority): AutomationTask | null {
    const task = this.tasks.find((t) => t.id === taskId)
    if (!task) return null
    task.status = 'QUEUED'
    task.priority = priority ?? task.priority
    task.error = null
    task.finishedAt = null
    this.sort()
    return task
  }

  requeueForRetry(task: AutomationTask): AutomationTask {
    const next: AutomationTask = {
      ...task,
      status: 'RETRYING',
      startedAt: null,
      finishedAt: null,
      duration: null,
    }
    this.tasks.push(next)
    this.sort()
    return next
  }

  list(): AutomationTask[] {
    return [...this.tasks]
  }

  size(): number {
    return this.peek().length
  }

  clear(): void {
    this.tasks.length = 0
  }

  private sort(): void {
    this.tasks.sort((a, b) => {
      const aReady = a.status === 'QUEUED' || a.status === 'RETRYING' ? 0 : 1
      const bReady = b.status === 'QUEUED' || b.status === 'RETRYING' ? 0 : 1
      if (aReady !== bReady) return aReady - bReady
      return b.priority - a.priority || a.createdAt.localeCompare(b.createdAt)
    })
  }
}

export const jobQueue = new JobQueue()
